import { CircularProgress, Grid, Typography } from '@mui/material';
import CreateSurveyFolderDialog from 'features/motive/motive-manage-surveys/create-survey-folder-dialog';
import CreateSurveyDialog from 'features/motive/motive-manage-surveys/create-survey-dialog';
import React from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { makeStyles } from 'tss-react/mui';
import BasicConfirmDialog from '../features/ui/base-dialogs/basic-confirm-dialog';
import EditSurveyFolderDialog from '../features/motive/motive-manage-surveys/edit-survey-folder-dialog';
import SurveyFolder from '../features/motive/motive-manage-surveys/survey-folder';
import ManageSurveysTopBar from '../features/motive/motive-manage-surveys/manage-surveys-top-bar';
import { ImageTypes } from '../enums';
import { useSurveyManagement, SelectionState } from '../features/motive/motive-manage-surveys/use-survey-management';
import { TagModel } from '../features/motive/survey.types';

interface TagPanelsProps {
  tags: TagModel[];
  tagsOrder: number[];
  selectionState: SelectionState;
  draggedAnalysisId: string;
  dynamicRoute: string;
  token: string;
  handleToggleVisibility: (tagId: string, visibility?: boolean) => () => void;
  handleCheckboxClick(analysisId: string): (event: React.ChangeEvent, checked: boolean) => void;
  deleteTag(tagId: string): void;
  handleEditTagClick(tagId: string): (event: React.SyntheticEvent) => void;
  handleOpenConfirmDeleteSurveyDialog(currentTag: TagModel): () => void;
}

const MotivEManageSurveys: React.FC = () => {
  const { classes } = useStyles();
  const {
    loading,
    csvLoading,
    tagsState,
    draggedAnalysisId,
    selectionState,
    dynamicRoute,
    token,
    t,
    // Send invitations
    sendInvitationsState,
    sendInvitationsFormState,
    onSendInvitations,
    handleExportInviteLinks,
    handleExportSelectedInviteLinks,
    selectedInvitationsCount,
    handleSendInvitationsClose,
    handleSelectedFolderChange,
    handleSelectedSurveyTypeChange,
    formChangeHandlerGenerator,
    formChangeHandleDeleteGenerator,
    addInvitations,
    clearInvitations,
    addInvitationsCSV,
    addInvitationsCSVFromDrop,
    handleSendInvitationsProceed,
    handleNotSendEmailChange,
    // Tag management
    newTagDialogState,
    editTagDialogState,
    handleNewTagClick,
    handleNewTagChange,
    handleNewTagDialogClose,
    handleNewTagProceed,
    handleEditTagClick,
    handleEditTagChange,
    handleEditTagClose,
    handleEditTagProceed,
    handleToggleVisibility,
    handleTagDelete,
    // Drag & drop
    handleOnDragStart,
    handleOnDragEnd,
    handleCheckboxClick,
    // Move dialog
    surveysMoveState,
    handleMoveDialogConfirm,
    handleMoveDialogClose,
    // Delete dialog
    confirmDeleteState,
    handleOpenConfirmDeleteSurveyDialog,
    handleCloseConfirmDeleteSurveyDialog,
    handleAnalysesDelete,
    csvImportSummary,
    csvPreviewRows,
  } = useSurveyManagement();

  return (
    <>
      <ManageSurveysTopBar
        handleNewTagClick={handleNewTagClick}
        onSendInvitations={onSendInvitations}
        onExportInviteLinks={handleExportInviteLinks}
        onExportSelectedInviteLinks={handleExportSelectedInviteLinks}
        selectedInvitationsCount={selectedInvitationsCount}
      />

      <div className={classes.content}>
        <Grid container>
          {loading ? (
            <Grid size={12} className={classes.loadingContainer}>
              <CircularProgress />
            </Grid>
          ) : (
            <Grid size={12}>
              <DragDropContext onDragEnd={handleOnDragEnd} onDragStart={handleOnDragStart}>
                <Droppable droppableId="all-tags" type="TAG">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      <SurveyFolderMemoized
                        handleToggleVisibility={handleToggleVisibility}
                        draggedAnalysisId={draggedAnalysisId}
                        tags={tagsState.tags}
                        tagsOrder={tagsState.tagsOrder}
                        selectionState={selectionState}
                        deleteTag={handleTagDelete}
                        handleCheckboxClick={handleCheckboxClick}
                        handleEditTagClick={handleEditTagClick}
                        handleOpenConfirmDeleteSurveyDialog={handleOpenConfirmDeleteSurveyDialog}
                        dynamicRoute={dynamicRoute}
                        token={token}
                      />
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Grid>
          )}
        </Grid>
      </div>

      {/* Create Survey Dialog */}
      <CreateSurveyDialog
        open={sendInvitationsState.open}
        loading={sendInvitationsState.loading}
        csvLoading={csvLoading}
        selectedFolder={sendInvitationsState.selectedFolder}
        folders={tagsState.tagsOrder.map((index) => ({
          label: tagsState.tags[index]?.tagName || '',
          value: tagsState.tags[index]?.tagId || '',
        }))}
        selectedSurveyType={sendInvitationsState.selectedSurveyType}
        surveyTypes={[
          { label: t('classic'), value: `${ImageTypes.CLASSIC_IMAGE}` },
          { label: t('modern'), value: `${ImageTypes.MODERN_IMAGE}` },
          { label: t('extended'), value: `${ImageTypes.EXTENDED_IMAGE}` },
        ]}
        sendInvitationsFormState={sendInvitationsFormState}
        csvImportSummary={csvImportSummary}
        csvPreviewRows={csvPreviewRows}
        notSendEmail={sendInvitationsState.notSendEmail}
        formChangeHandlerGenerator={formChangeHandlerGenerator}
        formChangeHandleDeleteGenerator={formChangeHandleDeleteGenerator}
        addInvitations={addInvitations}
        clearInvitations={clearInvitations}
        addInvitationsCSV={addInvitationsCSV}
        addInvitationsCSVFromDrop={addInvitationsCSVFromDrop}
        handleClose={handleSendInvitationsClose}
        handleProceed={handleSendInvitationsProceed}
        handleSelectedFolderChange={handleSelectedFolderChange}
        handleSelectedSurveyTypeChange={handleSelectedSurveyTypeChange}
        handleNotSendEmailChange={handleNotSendEmailChange}
      />

      {/* Edit Folder Dialog */}
      <EditSurveyFolderDialog
        open={editTagDialogState.open}
        tagName={editTagDialogState.tagName}
        handleClose={handleEditTagClose}
        handleProceed={handleEditTagProceed}
        handleTagNameChange={handleEditTagChange}
        loading={editTagDialogState.loading}
      />

      {/* Create Folder Dialog */}
      <CreateSurveyFolderDialog
        title={t('newSurvey')}
        open={newTagDialogState.open}
        tagName={newTagDialogState.tagName}
        handleClose={handleNewTagDialogClose}
        handleProceed={handleNewTagProceed}
        handleTagNameChange={handleNewTagChange}
        loading={newTagDialogState.loading}
      />

      {/* Move Confirmation Dialog */}
      <BasicConfirmDialog
        open={surveysMoveState.dialogOpen}
        title={t('switchTag')}
        confirmButton={t('switch')}
        cancelButton={t('cancel')}
        handleClose={handleMoveDialogClose}
        handleConfirm={handleMoveDialogConfirm}
        loading={surveysMoveState.loading}
      >
        <Typography variant="body1">
          {t('confirmSwitchTagSurveys', {
            count: surveysMoveState.analysesCount,
            source: surveysMoveState.sourceTagName,
            dest: surveysMoveState.destinationTagName,
          })}
        </Typography>
      </BasicConfirmDialog>

      {/* Delete Confirmation Dialog */}
      <BasicConfirmDialog
        open={confirmDeleteState.open}
        title={t('confirmDeleteTitle')}
        confirmButton={t('delete')}
        handleConfirm={confirmDeleteState.currentTag && handleAnalysesDelete(confirmDeleteState.currentTag.tagId)}
        cancelButton={t('cancel')}
        handleClose={handleCloseConfirmDeleteSurveyDialog}
      >
        <Typography variant="body1">{t('confirmDeleteSurveys', { count: confirmDeleteState.selectionCount })}</Typography>
      </BasicConfirmDialog>
    </>
  );
};

const SurveyFolderMemoized = React.memo(function TagPanels({
  tags,
  tagsOrder,
  draggedAnalysisId,
  handleCheckboxClick,
  selectionState,
  deleteTag,
  handleOpenConfirmDeleteSurveyDialog,
  handleEditTagClick,
  dynamicRoute,
  handleToggleVisibility,
  token,
}: TagPanelsProps) {
  return (
    <>
      {tagsOrder.map((tagIndex: number, index: number) => {
        const tag = tags[tagIndex];
        return (
          <SurveyFolder
            handleEditTagClick={handleEditTagClick}
            draggedAnalysisId={draggedAnalysisId}
            handleToggleVisibility={handleToggleVisibility}
            tag={tag}
            key={'panel' + tag.tagId}
            index={index}
            handleCheckboxClick={handleCheckboxClick}
            selectionState={selectionState}
            deleteTag={deleteTag}
            handleOpenConfirmDeleteSurveyDialog={handleOpenConfirmDeleteSurveyDialog}
            dynamicRoute={dynamicRoute}
            token={token}
          />
        );
      })}
    </>
  );
});

const useStyles = makeStyles()((theme) => ({
  content: {
    marginTop: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6, 0),
  },
}));

export default MotivEManageSurveys;

// Re-export types needed by child components
export type { SelectionState } from '../features/motive/motive-manage-surveys/use-survey-management';
export type { SingleForm } from '../features/motive/motive-manage-surveys/use-survey-management';
